import Image from '@/components/UI/Image';
import { EFCalculateIcon3, EFCalculateIcon4, EFCalculateIcon5, EFCalculateIcon6, EFCalculateIcon7 } from '@/components/UI/svg/Calculate';
import { useGlobal } from '@/utils/useGlobal';
import isEmpty from 'lodash/isEmpty';
import HowToConnect from '../HowToConnect';
import SuspendedButton from '../SuspendedButton';
import type { ICalcRecommendProduct, IProductItem, ISelectInfo, ISolarItem, IStep3Props } from '../cacl';
import css from './index.module.scss';
export default function Step3({ productList, solarList, shopifyPriceMap, selectInfo }: IStep3Props) {
    const { useState, useLang, useRouter, isMobile, gtm } = useGlobal();
    const { t } = useLang('calculatorssolarpanels');
    const router = useRouter();
    const { locale } = router;
    const [connectInfo, setConnectInfo] = useState({
        visible: false,
        howToConnectImg: '',
        solarNumber: 1,
    });

    const getDataLayer = (buttonName: string, optionName?: string, price?: string | number) => {
        const allProducts = productList.reduce<IProductItem[]>((p, c) => p.concat(c.list), []);
        const product_name = allProducts.find((item) => item.key === selectInfo.productkey)?.productName;
        return {
            event: 'uaEvent',
            event_name: 'calculator3_solar_panels',
            step: 'recommendations',
            button_name: buttonName,
            product_name: product_name,
            list_name: 'none',
            option_name: optionName || 'none',
            price: price ? priceUnit + price : 'none',
        };
    };

    const showHowToConnectModal = (howToConnectImg?: string, solarNumber?: number) => {
        setConnectInfo({
            visible: true,
            howToConnectImg: howToConnectImg || '',
            solarNumber: solarNumber || 1,
        });
    };

    const goBack = () => {
        router.push({
            pathname: `/${locale}/calculators/solarpanels`,
            query: {
                step: 2,
            },
        });
    };

    const tryAgain = () => {
        /** gtm 埋点 --start */
        const dataLayer = getDataLayer('Try Again');
        gtm.push(dataLayer);
        /** gtm 埋点 --end */

        router.push({
            pathname: `/${locale}/calculators/solarpanels`,
        });
    };

    /**
     * 查找商品数据
     */
    const findProductData = (products: ICalcRecommendProduct[], selectInfo: ISelectInfo): ICalcRecommendProduct | undefined => {
        const { chargTime, solarType } = selectInfo;
        if (!chargTime || !solarType) return;
        const calcProductBase = products.find((product) => {
            const { maxUseTime, minUseTime, type } = product;
            const typeList = type
                .split(',')
                .map((type) => type.trim())
                .filter((type) => !!type);

            return maxUseTime >= chargTime && minUseTime <= chargTime && typeList.includes(solarType);
        });
        if (!calcProductBase) return;
        // bundle混合太阳能板数据
        const currSolarPanelInfo: Partial<ISolarItem> = solarList.find((solar) => solar.key === calcProductBase?.solarKey) || {};
        if (isEmpty(currSolarPanelInfo)) return;

        const { weight, area, weidhtUnit, areaUnit, ...rest } = currSolarPanelInfo as ISolarItem;
        const { solarNumber } = calcProductBase;
        const totalWeight = Math.round(solarNumber * 100 * weight) / 100;
        const totalArea = Math.round(area * 100 * solarNumber) / 100;

        // 价格&折扣
        const priceObj = shopifyPriceMap[calcProductBase.bundleVariId] || {};
        const { compareAtPrice, price } = priceObj;
        priceObj.compareAtPrice = compareAtPrice || price || '???';
        priceObj.price = price || '???';
        const priceNumber = Number(price) || 0;
        const compareAtPriceNumber = Number(compareAtPrice) || priceNumber;
        priceObj.discount = Math.round(compareAtPriceNumber * 100 - priceNumber * 100) / 100;

        // how to connect img path
        const howToConnectImg = `/${isMobile ? 'h5img' : 'img'}/calculate/solarpanels/how_to_connect_${calcProductBase.solarNumber}.jpg`;

        return {
            ...calcProductBase,
            ...rest,
            ...priceObj,
            howToConnectImg,
            totalWeight: `${totalWeight} ${weidhtUnit}`,
            totalArea: `${totalArea} ${areaUnit}`,
        };
    };

    /**
     * 计算出最终的推荐商品 与 火爆商品
     * @returns
     */
    const getResCalcProducts = () => {
        if (typeof window === 'undefined') return {};
        const { productkey, chargTime, solarType } = selectInfo;
        if (!productkey || !chargTime || !solarType) {
            router.push({
                pathname: `/${locale}/calculators/solarpanels`,
            });
            return {};
        }

        // 找到第一步选择的电池商品信息
        const allPowerProducts = productList.reduce<IProductItem[]>((p, c) => p.concat(c.list), []);
        const currPoweProductInfo = allPowerProducts.find((product) => product.key === productkey);
        if (!currPoweProductInfo) return {};

        return {
            recommentProduct: findProductData(currPoweProductInfo.calcRecommendProducts, selectInfo),
            bestSalesProduct: findProductData(currPoweProductInfo.calcBestSalesProducts, selectInfo),
        };
    };

    /**
     * 获取金额单位
     */
    const getPriceUnit = (locale: string): string => {
        const priceUnitList = [{ locale: 'us', unit: '$' }];
        const obj = priceUnitList.find((unit) => unit.locale === locale);
        return obj?.unit || '$';
    };

    /**
     * 购买
     */
    const buy = (bundleVariId?: string, bundleName?: string, price?: string | number) => {
        if (!bundleVariId) return;
        /** gtm 埋点 --start */
        const dataLayer = getDataLayer(t('calc_step3_buy_btn'), bundleName, price);
        gtm.push(dataLayer);
        /** gtm 埋点 --end */

        const winOpen = window.open;
        const buyLink = `https://${locale}.ecoflow.com/cart/${bundleVariId}:1`;
        winOpen(buyLink, 'solarpanel-calculators-shopify');
    };

    /**
     * get all title
     */
    const getTitle = (solarNumber?: string | number, rawTitle?: string) => {
        return Number(solarNumber) > 1 ? `${solarNumber || ''} x ${rawTitle || ''}` : rawTitle;
    };

    const priceUnit = getPriceUnit(locale);
    const { recommentProduct, bestSalesProduct } = getResCalcProducts();

    return (
        <div className={css.step3}>
            <h3 className="calc-solar-step3-title">{t('cacl_step3_title')}</h3>
            <div className="calc-product-list">
                {/* 推荐商品 */}
                <div className="calc-product-item recommend">
                    <div className="recommend-tag">
                        <span className="recommend-icon">
                            <EFCalculateIcon3 />
                        </span>
                        <span className="recommend-text font-family-semibold recommend">{t('cacl_step3_recomment_text')}</span>
                    </div>
                    <h5 className="product-title">{getTitle(recommentProduct?.solarNumber, recommentProduct?.title)}</h5>
                    <h6 className="product-sub-title font-family-semibold">{recommentProduct?.subTitle}</h6>
                    <div className="main-img">
                        <Image src={isMobile ? recommentProduct?.mainImgH5 : recommentProduct?.mainImgPc} layout="fill" alt={recommentProduct?.title} />
                    </div>
                    <a
                        className="how-wo-connect font-family-regular"
                        onClick={() => showHowToConnectModal(recommentProduct?.howToConnectImg, recommentProduct?.solarNumber)}
                    >
                        {t('calc_step3_how_to_connect')}
                    </a>
                    <div className="product-props-wrapper">
                        <div className="props-item left">
                            <EFCalculateIcon5 />
                            <span className="props-name ont-family-medium">{t('calc_step3_recharge_time_name')}</span>
                            <b className="props-value font-family-semibold">{recommentProduct?.rechargeTimeShow}</b>
                        </div>
                        <div className="props-item right">
                            <EFCalculateIcon6 />
                            <span className="props-name ont-family-medium">{t('calc_step3_total_area_name')}</span>
                            <b className="props-value font-family-semibold">{recommentProduct?.totalArea}</b>
                        </div>
                        <div className="props-item left">
                            <EFCalculateIcon7 />
                            <span className="props-name font-family-medium">{t('calc_step3_total_weight_name')}</span>
                            <b className="props-value font-family-semibold">{recommentProduct?.totalWeight}</b>
                        </div>
                    </div>
                    <div className="product-buy-wrapper">
                        <div className="price-wrapper">
                            <div className="price-content">
                                <span className="price font-family-semibold">
                                    <i>{priceUnit}</i>
                                    <i>{recommentProduct?.price}</i>
                                </span>
                                <span className="origin-price font-family-medium">
                                    <i>{priceUnit}</i>
                                    <i>{recommentProduct?.compareAtPrice}</i>
                                </span>
                            </div>
                            <div className="discount font-family-semibold">
                                {t('calc_step3_discount_text')} {priceUnit}
                                {recommentProduct?.discount}
                            </div>
                        </div>

                        <button
                            onClick={() =>
                                buy(recommentProduct?.bundleVariId, getTitle(recommentProduct?.solarNumber, recommentProduct?.title), recommentProduct?.price)
                            }
                            className="buy-bth"
                        >
                            {t('calc_step3_buy_btn')}
                        </button>
                    </div>
                </div>

                {/* 火爆商品 */}
                <div className="calc-product-item best-sale">
                    <div className="recommend-tag best-sale">
                        <span className="recommend-icon">
                            <EFCalculateIcon4 />
                        </span>
                        <span className="recommend-text best-sale font-family-semibold">{t('cacl_step3_best_sale_text')}</span>
                    </div>
                    <h5 className="product-title">{getTitle(bestSalesProduct?.solarNumber, bestSalesProduct?.title)}</h5>
                    <h6 className="product-sub-title font-family-semibold">{bestSalesProduct?.subTitle}</h6>
                    <div className="main-img">
                        <Image src={isMobile ? bestSalesProduct?.mainImgH5 : bestSalesProduct?.mainImgPc} layout="fill" alt={bestSalesProduct?.title} />
                    </div>
                    <a
                        className="how-wo-connect font-family-regular"
                        onClick={() => showHowToConnectModal(bestSalesProduct?.howToConnectImg, bestSalesProduct?.solarNumber)}
                    >
                        {t('calc_step3_how_to_connect')}
                    </a>
                    <div className="product-props-wrapper">
                        <div className="props-item left">
                            <EFCalculateIcon5 />
                            <span className="props-name ont-family-medium">{t('calc_step3_recharge_time_name')}</span>
                            {/* 使用推荐商品的充电时间范围？？ */}
                            <b className="props-value font-family-semibold">{bestSalesProduct?.rechargeTimeShow}</b>
                        </div>
                        <div className="props-item right">
                            <EFCalculateIcon6 />
                            <span className="props-name ont-family-medium">{t('calc_step3_total_area_name')}</span>
                            <b className="props-value font-family-semibold">{bestSalesProduct?.totalArea}</b>
                        </div>
                        <div className="props-item left">
                            <EFCalculateIcon7 />
                            <span className="props-name font-family-medium">{t('calc_step3_total_weight_name')}</span>
                            <b className="props-value font-family-semibold">{bestSalesProduct?.totalWeight}</b>
                        </div>
                    </div>
                    <div className="product-buy-wrapper">
                        <div className="price-wrapper">
                            <div className="price-content">
                                <span className="price font-family-semibold">
                                    <i>{priceUnit}</i>
                                    <i>{bestSalesProduct?.price}</i>
                                </span>
                                <span className="origin-price font-family-medium">
                                    <i>{priceUnit}</i>
                                    <i>{bestSalesProduct?.compareAtPrice}</i>
                                </span>
                            </div>
                            <div className="discount font-family-semibold">
                                {t('calc_step3_discount_text')} {priceUnit}
                                {bestSalesProduct?.discount}
                            </div>
                        </div>

                        <button
                            onClick={() =>
                                buy(bestSalesProduct?.bundleVariId, getTitle(bestSalesProduct?.solarNumber, bestSalesProduct?.title), bestSalesProduct?.price)
                            }
                            className="buy-bth"
                        >
                            {t('calc_step3_buy_btn')}
                        </button>
                    </div>
                </div>
            </div>
            <SuspendedButton goBack={goBack} goNext={tryAgain} nextText="Try Again" />
            <HowToConnect
                howToConnectImg={connectInfo.howToConnectImg}
                visible={connectInfo.visible}
                solarNumber={connectInfo.solarNumber}
                title={t('calc_step3_how_to_connect_title')}
                onCancel={() => setConnectInfo({ ...connectInfo, visible: false })}
            />
        </div>
    );
}
